import React, { useEffect, useState, useRef, useCallback } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Chip,
    Box,
    Typography,
    TextField,
    InputAdornment,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { startHubSpotPreview, getHubSpotPreview } from 'actions/hubspot'

// Cap on rendered rows so a very large sync can't flood the DOM; filters narrow below this.
const MAX_ROWS = 200
// How often to poll the background preview job while it runs.
const POLL_MS = 2500

const unwrap = (res) => res?.response || res?.responseBody || res || null

// Client-wide read-only dry-run of the scheduled sync, both directions. Runs in the background
// (POST to start, poll GET) so a large account can't time out the request.
const HubSpotSyncPreviewDialog = ({ open, onClose }) => {
    const [status, setStatus] = useState('RUNNING') // RUNNING | COMPLETED | FAILED
    const [preview, setPreview] = useState(null)
    const [error, setError] = useState(null)
    const [direction, setDirection] = useState('inbound')
    const [inFilter, setInFilter] = useState('all')
    const [outFilter, setOutFilter] = useState('all')
    const [search, setSearch] = useState('')
    // Tracks the active poll loop so opening/re-running/closing cancels any prior one.
    const pollRef = useRef({ cancelled: false, timer: null })

    // forceNew=true starts a brand-new run; otherwise resume a running job / show a finished one,
    // and only start a fresh run when there's nothing usable yet.
    const beginRun = useCallback((forceNew) => {
        pollRef.current.cancelled = true
        if (pollRef.current.timer) clearTimeout(pollRef.current.timer)
        const token = { cancelled: false, timer: null }
        pollRef.current = token

        setStatus('RUNNING')
        setPreview(null)
        setError(null)

        const applyTerminal = (job) => {
            if (job?.status === 'COMPLETED') { setPreview(job.result || null); setStatus('COMPLETED'); return true }
            if (job?.status === 'FAILED') { setError(job.errorMessage || 'The preview run failed.'); setStatus('FAILED'); return true }
            return false
        }
        const poll = () => {
            getHubSpotPreview()
                .then((res) => { if (!token.cancelled && !applyTerminal(unwrap(res))) token.timer = setTimeout(poll, POLL_MS) })
                .catch(() => { if (!token.cancelled) { setError('Failed to load the preview.'); setStatus('FAILED') } })
        }
        const startAndPoll = () => startHubSpotPreview()
            .then(() => { if (!token.cancelled) poll() })
            .catch(() => { if (!token.cancelled) { setError('Failed to start the preview.'); setStatus('FAILED') } })

        if (forceNew) { startAndPoll(); return }

        // Resume: show an existing result, keep polling a running one, or start fresh if there's none.
        getHubSpotPreview()
            .then((res) => {
                if (token.cancelled) return
                const job = unwrap(res)
                if (job?.status === 'COMPLETED') { setPreview(job.result || null); setStatus('COMPLETED') }
                else if (job?.status === 'RUNNING') { poll() }
                else { startAndPoll() } // NONE / FAILED — start a fresh run
            })
            .catch(() => { if (!token.cancelled) startAndPoll() })
    }, [])

    useEffect(() => {
        if (!open) return undefined
        setDirection('inbound')
        setInFilter('all')
        setOutFilter('all')
        setSearch('')
        beginRun(false)
        return () => { pollRef.current.cancelled = true; if (pollRef.current.timer) clearTimeout(pollRef.current.timer) }
    }, [open, beginRun])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='sm'
            fullWidth
            aria-labelledby='hubspot-preview-title'
            PaperProps={{ style: { maxHeight: '82vh' } }}
        >
            <DialogTitle id='hubspot-preview-title'>Preview scheduled HubSpot sync</DialogTitle>
            <DialogContent dividers style={{ padding: 0, overflowY: 'hidden' }}>
                {status === 'RUNNING' && (
                    <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' style={{ padding: 48, gap: 14 }}>
                        <CircularProgress size={28} />
                        <Typography variant='body2' style={{ color: '#6c757d', textAlign: 'center' }}>
                            Running the preview in the background…<br />
                            <span style={{ color: '#adb5bd' }}>Large accounts can take a few minutes. You can leave this open.</span>
                        </Typography>
                    </Box>
                )}

                {status === 'FAILED' && (
                    <Box style={{ padding: 32 }}>
                        <Typography variant='body2' style={{ color: '#c62828' }}>
                            {error || 'The preview run failed.'}
                        </Typography>
                    </Box>
                )}

                {status === 'COMPLETED' && preview && (
                    <>
                        {/* Direction toggle */}
                        <Box px={2.5} pt={2} pb={1.5} display='flex' style={{ gap: 8, borderBottom: '1px solid #eee' }}>
                            {tab('inbound', 'HubSpot → Rox', direction, setDirection)}
                            {tab('outbound', 'Rox → HubSpot', direction, setDirection)}
                        </Box>

                        {/* Search */}
                        <Box px={2.5} py={1.5} style={{ borderBottom: '1px solid #eee' }}>
                            <TextField
                                fullWidth
                                size='small'
                                variant='outlined'
                                placeholder='Search by business name…'
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <SearchIcon fontSize='small' style={{ color: '#adb5bd' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        {direction === 'inbound'
                            ? renderInbound(preview, inFilter, setInFilter, search)
                            : renderOutbound(preview, outFilter, setOutFilter, search)}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {status !== 'RUNNING' && (
                    <Button onClick={() => beginRun(true)} color='primary'>
                        Re-run
                    </Button>
                )}
                <Button onClick={onClose} color='primary'>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}

// ---- Inbound (HubSpot → Rox): deals matching applications ----

const renderInbound = (preview, filter, setFilter, search) => {
    const deals = preview.deals || []
    const wouldSync = deals.filter((d) => d.wouldMatch).length
    const newMatches = deals.filter((d) => d.wouldMatch && !d.alreadySynced).length
    const alreadySynced = wouldSync - newMatches
    const nameOnly = deals.filter((d) => d.nameMatch && !d.wouldMatch).length
    const noName = deals.filter((d) => !d.nameMatch).length

    const q = (search || '').trim().toLowerCase()
    const filtered = deals
        .filter((d) => filter === 'all' || inBucket(d) === filter)
        .filter((d) => !q || (d.dealName || '').toLowerCase().includes(q))
        .sort((a, b) => inRank(a) - inRank(b))
    const shown = filtered.slice(0, MAX_ROWS)

    return (
        <>
            <Box px={2.5} py={2} style={{ borderBottom: '1px solid #eee', background: 'var(--bg-canvas)' }}>
                <Typography variant='caption' style={{ color: 'var(--fg-tertiary)' }}>
                    HubSpot deals matched to a Rox application ({preview.dealsProcessed} total). Notes sync into Rox. Nothing is written.
                </Typography>
                {filterBar([
                    { key: 'new', label: 'New', count: newMatches, color: C.green },
                    { key: 'synced', label: 'Already synced', count: alreadySynced, color: C.blue },
                    { key: 'name', label: 'Name only', count: nameOnly, color: C.amber },
                    { key: 'noname', label: 'No name', count: noName, color: C.grey },
                ], preview.dealsProcessed, filter, setFilter)}
            </Box>

            <Box style={{ overflowY: 'auto', maxHeight: '50vh' }}>
                {shown.length === 0 && emptyLine(deals.length === 0
                    ? 'No HubSpot deals were returned for this account (last 3 months).'
                    : 'No deals in this filter.')}
                {shown.map((deal, i) => row(deal.dealId || i, deal.dealName, deal.amount, inStatusOf(deal), inReasonOf(deal)))}
                {moreLine(filtered.length)}
            </Box>
        </>
    )
}

// ---- Outbound (Rox → HubSpot): apps pushed out as new deals ----

const renderOutbound = (preview, filter, setFilter, search) => {
    const apps = preview.outbound || []
    const counts = apps.reduce((acc, a) => {
        const code = a.reasonCode || 'OTHER'
        acc[code] = (acc[code] || 0) + 1
        return acc
    }, {})

    const q = (search || '').trim().toLowerCase()
    const filtered = apps
        .filter((a) => filter === 'all' || a.reasonCode === filter)
        .filter((a) => !q || (a.businessName || '').toLowerCase().includes(q))
        .sort((a, b) => OUT_ORDER.indexOf(a.reasonCode) - OUT_ORDER.indexOf(b.reasonCode))
    const shown = filtered.slice(0, MAX_ROWS)

    return (
        <>
            <Box px={2.5} py={2} style={{ borderBottom: '1px solid #eee', background: 'var(--bg-canvas)' }}>
                <Typography variant='caption' style={{ color: 'var(--fg-tertiary)' }}>
                    Rox applications with no matching deal that would be created in HubSpot as a new deal.
                </Typography>
                {!preview.outboundEnabled && (
                    <Box mt={1} p={1} style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6 }}>
                        <Typography variant='caption' style={{ color: '#8d6e00' }}>
                            Outbound is off — a real run creates nothing. Enable “Create HubSpot deals from Rox
                            applications” to push these.
                        </Typography>
                    </Box>
                )}
                {filterBar(
                    OUT_ORDER.map((code) => ({
                        key: code,
                        label: OUT_REASONS[code].label,
                        count: counts[code] || 0,
                        color: OUT_REASONS[code].color,
                    })),
                    preview.applicationsProcessed,
                    filter,
                    setFilter,
                )}
            </Box>

            <Box style={{ overflowY: 'auto', maxHeight: '50vh' }}>
                {shown.length === 0 && emptyLine(apps.length === 0
                    ? 'No unmatched Rox applications to push.'
                    : 'No applications in this filter.')}
                {shown.map((app, i) => row(
                    app.applicationId || i,
                    app.businessName || '(no business name)',
                    null,
                    app.wouldCreate ? STATUS.CREATE : STATUS.SKIP,
                    app.reason,
                ))}
                {moreLine(filtered.length)}
            </Box>
        </>
    )
}

// ---- shared rendering ----

const row = (key, title, amount, status, reason) => (
    <Box
        key={key}
        px={2.5}
        py={1.5}
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        style={{ borderBottom: '1px solid #f0f0f0' }}
    >
        <Box display='flex' alignItems='center' style={{ minWidth: 0, flex: 1, gap: 10 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: status.dot, flexShrink: 0 }} />
            <Box style={{ minWidth: 0, overflow: 'hidden' }}>
                <Typography variant='body2' noWrap style={{ fontWeight: 500, color: '#212529' }}>
                    {title}
                    {amount && <span style={{ color: '#adb5bd', fontWeight: 400, marginLeft: 8 }}>${amount}</span>}
                </Typography>
                <Typography variant='caption' style={{ color: '#868e96' }} noWrap>
                    {reason}
                </Typography>
            </Box>
        </Box>
        <Chip
            size='small'
            label={status.label}
            style={{ background: status.bg, color: status.fg, flexShrink: 0, marginLeft: 8 }}
        />
    </Box>
)

const emptyLine = (text) => (
    <Box px={2.5} py={3}>
        <Typography variant='body2' style={{ color: '#999' }}>{text}</Typography>
    </Box>
)

const moreLine = (total) =>
    total > MAX_ROWS && (
        <Box px={2.5} py={1.5}>
            <Typography variant='caption' style={{ color: '#adb5bd' }}>
                Showing first {MAX_ROWS} of {total}. Use the filters above to narrow down.
            </Typography>
        </Box>
    )

const tab = (key, label, active, setActive) => (
    <Chip
        label={label}
        onClick={() => setActive(key)}
        style={{
            background: active === key ? 'var(--rox-dark-green)' : 'transparent',
            color: active === key ? '#fff' : 'var(--neutral-700)',
            border: `1px solid ${active === key ? 'var(--rox-dark-green)' : '#dee2e6'}`,
            cursor: 'pointer',
            fontWeight: active === key ? 600 : 400,
        }}
    />
)

// A proportional distribution bar plus a clickable legend that doubles as the filter.
// segments: [{ key, label, count, color }]. Click a legend item to filter to it; click again to clear.
const filterBar = (segments, total, active, setFilter) => {
    const sum = total || segments.reduce((a, s) => a + s.count, 0)
    const present = segments.filter((s) => s.count > 0)
    return (
        <Box mt={1.5}>
            <Box style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#eef0f2' }}>
                {present.map((s) => (
                    <Box
                        key={s.key}
                        title={`${s.label}: ${s.count}`}
                        onClick={() => setFilter(active === s.key ? 'all' : s.key)}
                        style={{
                            width: `${sum ? (s.count / sum) * 100 : 0}%`,
                            background: s.color,
                            cursor: 'pointer',
                            opacity: active === 'all' || active === s.key ? 1 : 0.3,
                        }}
                    />
                ))}
            </Box>
            <Box display='flex' flexWrap='wrap' style={{ gap: '6px 18px', marginTop: 10 }}>
                {segments.map((s) => {
                    const isActive = active === s.key
                    const dim = active !== 'all' && !isActive
                    return (
                        <Box
                            key={s.key}
                            onClick={() => s.count > 0 && setFilter(isActive ? 'all' : s.key)}
                            display='flex'
                            alignItems='center'
                            style={{ gap: 6, cursor: s.count > 0 ? 'pointer' : 'default', opacity: dim ? 0.4 : s.count === 0 ? 0.5 : 1 }}
                        >
                            <span style={{ width: 9, height: 9, borderRadius: '50%', background: s.color }} />
                            <Typography variant='caption' style={{ fontWeight: isActive ? 700 : 500, color: 'var(--neutral-700)' }}>
                                {s.label}
                            </Typography>
                            <Typography variant='caption' style={{ color: '#adb5bd' }}>{s.count}</Typography>
                        </Box>
                    )
                })}
            </Box>
        </Box>
    )
}

// Segment colors for the distribution bar + legend dots.
const C = { green: '#43a047', blue: '#42a5f5', amber: '#fb8c00', slate: '#90a4ae', grey: '#ced4da' }

// Outbound skip/create reason codes → legend label + color, in display order.
const OUT_ORDER = ['WOULD_CREATE', 'NAME_EXISTS', 'NO_OWNER_EMAIL', 'NO_BUSINESS_NAME']
const OUT_REASONS = {
    WOULD_CREATE: { label: 'Would create', color: C.green },
    NAME_EXISTS: { label: 'Deal exists', color: C.slate },
    NO_OWNER_EMAIL: { label: 'No owner email', color: C.amber },
    NO_BUSINESS_NAME: { label: 'No business name', color: C.grey },
}

const STATUS = {
    NEW_MATCH: { label: 'New match', bg: 'var(--badge-approved-bg)', fg: 'var(--status-approved)', dot: '#43a047' },
    ALREADY_SYNCED: { label: 'Already synced', bg: 'var(--badge-review-bg)', fg: 'var(--rox-lilac-ink)', dot: '#42a5f5' },
    NAME_ONLY: { label: 'No email match', bg: 'var(--badge-watch-bg)', fg: '#e65100', dot: '#fb8c00' },
    NO_NAME: { label: 'No name match', bg: '#f1f3f5', fg: '#868e96', dot: '#ced4da' },
    CREATE: { label: 'Would create', bg: 'var(--badge-approved-bg)', fg: 'var(--status-approved)', dot: '#43a047' },
    SKIP: { label: 'Skipped', bg: '#f1f3f5', fg: '#868e96', dot: '#ced4da' },
}

const inBucket = (deal) => {
    if (deal.wouldMatch) return deal.alreadySynced ? 'synced' : 'new'
    if (deal.nameMatch) return 'name'
    return 'noname'
}

const inStatusOf = (deal) => {
    if (deal.wouldMatch) return deal.alreadySynced ? STATUS.ALREADY_SYNCED : STATUS.NEW_MATCH
    if (deal.nameMatch) return STATUS.NAME_ONLY
    return STATUS.NO_NAME
}

const inReasonOf = (deal) => {
    if (deal.destinationIssue) return deal.destinationIssue
    if (deal.wouldMatch) {
        const on = deal.matchedContactEmail ? ` · matched on ${deal.matchedContactEmail}` : ''
        return (deal.alreadySynced ? 'Already linked to a Rox application' : 'New match — a run would link it') + on
    }
    if (deal.nameMatch) return 'No deal contact email matches an owner'
    return 'No Rox application with this business name'
}

// matched (0) < name-only (1) < no-match (2)
const inRank = (deal) => (deal.wouldMatch ? 0 : deal.nameMatch ? 1 : 2)

export default HubSpotSyncPreviewDialog
